import React, { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";

import { AppUserApi } from "../../../api";
import { CustomBreadCrumb } from "../../../components/customBreadCrumb";
import { DailySummaryType, FoodLogType } from "../../../DTO/app-user/food";
import { useAppNavigate, useAppParams } from "../../../Router/hook";
import { selectUser } from "../../../redux-config/entities/user";
import { useAppSelector } from "../../../redux-config/hooks";
import { formatUserId } from "../../../utils";
import "./style.scss";

const LIGHT_COLORS: Record<string, string> = {
    green: "#22C55E",
    amber: "#F59E0B",
    yellow: "#F59E0B",
    red: "#EF4444",
    no_data: "#6B7280",
};

const PRIMARY_NUTRITION_KEYS = ["calories", "protein", "carbohydrates", "fat"];
const SECONDARY_NUTRITION_KEYS = [
    "fiber",
    "sugar",
    "sodium",
    "cholesterol",
    "saturated_fat",
    "trans_fat",
];

const prettifyKey = (key: string) =>
    key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const getLightColor = (light?: string) => LIGHT_COLORS[light || ""] || LIGHT_COLORS.red;

const getLightBadgeVariant = (light?: string) => {
    if (light === "green") return "success" as const;
    if (light === "amber" || light === "yellow") return "warning" as const;
    if (light === "no_data") return "secondary" as const;
    return "danger" as const;
};

const getMetricUnit = (key: string) => {
    if (key === "calories") return "kcal";
    if (key === "sodium" || key === "cholesterol") return "mg";
    return "g";
};

const getNumericValue = (source: Record<string, any>, key: string) => {
    const value = source?.[key];
    return typeof value === "number" ? value : Number(value || 0);
};

const toPercent = (value: number, max: number) => {
    if (!max) return 0;
    return Math.max(0, Math.min(100, (value / max) * 100));
};

const renderFoodLogMeta = (foodLog: FoodLogType) => {
    const meta: string[] = [];

    if (foodLog.portion) meta.push(`Portion: ${foodLog.portion}`);
    if (typeof foodLog.duration_time === "number") meta.push(`Duration: ${foodLog.duration_time} min`);
    if (typeof foodLog.on_time === "boolean") meta.push(foodLog.on_time ? "On time" : "Off time");
    if (typeof foodLog.after_midnight === "boolean" && foodLog.after_midnight) {
        meta.push("After midnight");
    }

    return meta;
};

export const DailySummaryPage: FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [summary, setSummary] = useState<DailySummaryType | null>(null);

    const { userId, dayId } = useAppParams();
    const navigate = useAppNavigate();
    const user = useAppSelector(selectUser);

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "Users", href: "/app-user" },
        {
            name: `User ${formatUserId(userId)}`,
            href: `/app-user/${userId}/progress/detail`,
        },
        { name: "Daily Summary", href: "#" },
    ];

    const fetchDailySummary = () => {
        if (!userId || !dayId) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Invalid parameters",
            });
            return;
        }

        setLoading(true);
        AppUserApi.DailySummary({
            appUser: user,
            userId: Number(userId),
            dayId: Number(dayId),
        })
            .then((data) => setSummary(data.daily_summary))
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: err || "Failed to load daily summary",
                });
                navigate(-1);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchDailySummary();
    }, [userId, dayId]);

    const foodLogs = useMemo(() => summary?.food_logs || [], [summary?.food_logs]);

    if (loading) {
        return (
            <Container
                fluid
                className="py-5 d-flex justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (!summary || !summary.has_summary) {
        return (
            <Container fluid className="dfs-page py-4">
                <CustomBreadCrumb breadCrumbList={crumbs} />
                <Alert variant="warning" className="mt-3 mb-0">
                    <strong>No data available</strong> - No summary found for this day.
                </Alert>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Daily Food Summary - Admin Panel</title>
            </Helmet>

            <Container fluid className="dfs-page py-3 py-md-4">
                <CustomBreadCrumb breadCrumbList={crumbs} />

                <Card className="dfs-header-card border-0 shadow-sm mt-3">
                    <Card.Body className="p-3 p-md-4">
                        <div className="dfs-header-row">
                            <div className="dfs-title-wrap">
                                <div className="dfs-emoji" aria-hidden="true">
                                    {summary.display.emoji || "-"}
                                </div>
                                <div>
                                    <div className="dfs-label">Daily Summary</div>
                                    <h1 className="dfs-title mb-1">{summary.display.title || "Day summary"}</h1>
                                    <div className="dfs-subtitle">
                                        Generated {formatDateTime(summary.generated_at)}
                                    </div>
                                </div>
                            </div>

                            <div className="dfs-header-actions">
                                <Badge
                                    bg={getLightBadgeVariant(summary.display.light)}
                                    className="dfs-status-badge"
                                >
                                    {summary.display.light.toUpperCase()}
                                </Badge>
                                <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                                    Back
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Row className="g-2 g-md-3 mt-1">
                    <Col xs={6} md={3}>
                        <Card className="dfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="dfs-kpi-label">Score</div>
                                <div className="dfs-kpi-value">{summary.score.percentage}%</div>
                                <div className="dfs-kpi-meta">
                                    {summary.score.total}/{summary.score.max} points
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="dfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="dfs-kpi-label">Worst Category</div>
                                <div className="dfs-kpi-value dfs-kpi-value-sm text-capitalize">
                                    {summary.insights.worst_category || "-"}
                                </div>
                                <div className="dfs-kpi-meta">Primary focus area</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="dfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="dfs-kpi-label">Meals Logged</div>
                                <div className="dfs-kpi-value">{foodLogs.length}</div>
                                <div className="dfs-kpi-meta">Food logs for this day</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="dfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="dfs-kpi-label">Protein Bonus</div>
                                <div className="dfs-kpi-value dfs-kpi-value-sm">
                                    {summary.protein_bonus.earned ? `+${summary.protein_bonus.points}` : "Not earned"}
                                </div>
                                <div className="dfs-kpi-meta">
                                    {summary.protein_bonus.high_protein_meals}/
                                    {summary.protein_bonus.required_meals} meals
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col xl={8}>
                        <Card className="dfs-section-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="dfs-section-head">
                                    <h2 className="dfs-section-title mb-0">Category Breakdown</h2>
                                    <span className="dfs-section-note">4 nutrition categories</span>
                                </div>

                                <div className="dfs-category-grid">
                                    {Object.entries(summary.scores_breakdown).map(([category, data]) => (
                                        <div className="dfs-category-tile" key={category}>
                                            <div className="d-flex justify-content-between align-items-center gap-2">
                                                <div className="fw-semibold text-capitalize">{category}</div>
                                                <Badge bg={getLightBadgeVariant(data.light)}>
                                                    {String(data.light).toUpperCase()}
                                                </Badge>
                                            </div>
                                            <div className="dfs-category-score mt-2">
                                                {data.points}
                                                <span>/{data.max}</span>
                                            </div>
                                            <div className="dfs-mini-bar mt-2" aria-hidden="true">
                                                <span
                                                    style={{
                                                        width: `${toPercent(data.points, data.max)}%`,
                                                        backgroundColor: getLightColor(data.light),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl={4}>
                        <Card className="dfs-section-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="dfs-section-head">
                                    <h2 className="dfs-section-title mb-0">Guidance</h2>
                                </div>

                                <div className="dfs-guidance-block">
                                    <div className="dfs-guidance-label">Today Insight</div>
                                    <div className="dfs-guidance-text">
                                        {summary.insights.daily_insight || "-"}
                                    </div>
                                </div>

                                <div className="dfs-guidance-block">
                                    <div className="dfs-guidance-label">Tomorrow Focus</div>
                                    <div className="dfs-guidance-text">
                                        {summary.insights.tomorrow_focus || "-"}
                                    </div>
                                </div>

                                {summary.suggestions?.length > 0 && (
                                    <div className="dfs-guidance-block">
                                        <div className="dfs-guidance-label">Suggestions</div>
                                        <ul className="dfs-list mb-0">
                                            {summary.suggestions.map((suggestion, index) => (
                                                <li key={`suggestion-${index}`}>{suggestion}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {summary.ai_summary && (
                                    <details className="dfs-details mt-2">
                                        <summary>AI Summary</summary>
                                        <div className="dfs-details-body">{summary.ai_summary}</div>
                                    </details>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col lg={12}>
                        <Card className="dfs-section-card border-0 shadow-sm">
                            <Card.Body>
                                <div className="dfs-section-head">
                                    <h2 className="dfs-section-title mb-0">Nutrition Snapshot</h2>
                                    <span className="dfs-section-note">Totals for selected day</span>
                                </div>

                                <div className="dfs-metric-grid">
                                    {PRIMARY_NUTRITION_KEYS.map((key) => (
                                        <div className="dfs-metric-card" key={key}>
                                            <div className="dfs-metric-label">{prettifyKey(key)}</div>
                                            <div className="dfs-metric-value">
                                                {getNumericValue(summary.nutrition_totals as Record<string, any>, key)}
                                            </div>
                                            <div className="dfs-metric-unit">{getMetricUnit(key)}</div>
                                        </div>
                                    ))}
                                </div>

                                <details className="dfs-details mt-3">
                                    <summary>More Nutrition Metrics</summary>
                                    <div className="dfs-details-body">
                                        <div className="dfs-secondary-grid">
                                            {SECONDARY_NUTRITION_KEYS.map((key) => (
                                                <div className="dfs-secondary-row" key={key}>
                                                    <span>{prettifyKey(key)}</span>
                                                    <span>
                                                        {getNumericValue(
                                                            summary.nutrition_totals as Record<string, any>,
                                                            key,
                                                        )} {getMetricUnit(key)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </details>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {foodLogs.length > 0 && (
                    <Row className="g-3 mt-1">
                        <Col lg={12}>
                            <Card className="dfs-section-card border-0 shadow-sm">
                                <Card.Body>
                                    <div className="dfs-section-head">
                                        <h2 className="dfs-section-title mb-0">Food Logs</h2>
                                        <span className="dfs-section-note">{foodLogs.length} meal log(s)</span>
                                    </div>

                                    <div className="dfs-food-log-list">
                                        {foodLogs.map((foodLog) => {
                                            const trafficLight = foodLog.traffic_light?.overall?.light;
                                            const metaChips = renderFoodLogMeta(foodLog);
                                            return (
                                                <div className="dfs-food-log-card" key={foodLog.id}>
                                                    <div className="dfs-food-log-head">
                                                        <div className="dfs-food-log-title-wrap">
                                                            <div className="dfs-food-log-title text-capitalize">
                                                                {foodLog.type}
                                                            </div>
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {foodLog.is_side && (
                                                                    <Badge bg="secondary">Side</Badge>
                                                                )}
                                                                <Badge
                                                                    bg={
                                                                        foodLog.status === "submitted"
                                                                            ? "success"
                                                                            : "secondary"
                                                                    }
                                                                >
                                                                    {foodLog.status}
                                                                </Badge>
                                                                {trafficLight && (
                                                                    <Badge bg={getLightBadgeVariant(trafficLight)}>
                                                                        {trafficLight}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="dfs-food-log-time">
                                                            {formatDateTime(foodLog.created_at)}
                                                        </div>
                                                    </div>

                                                    {foodLog.attachments?.length > 0 && (
                                                        <div className="dfs-thumb-grid">
                                                            {foodLog.attachments.map((attachment) => (
                                                                <a
                                                                    href={attachment.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    key={attachment.id}
                                                                    className="dfs-thumb-link"
                                                                    aria-label={`Open food image ${attachment.id}`}
                                                                >
                                                                    <img
                                                                        src={attachment.url}
                                                                        alt={
                                                                            attachment.original_name ||
                                                                            attachment.name ||
                                                                            `Food log ${foodLog.id}`
                                                                        }
                                                                        className="dfs-thumb"
                                                                        loading="lazy"
                                                                    />
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="dfs-food-log-section">
                                                        <div className="dfs-food-log-section-title">Items</div>
                                                        {foodLog.items?.length > 0 ? (
                                                            <div className="dfs-item-list">
                                                                {foodLog.items.map((item) => (
                                                                    <div className="dfs-item-row" key={item.id}>
                                                                        <div>
                                                                            <div className="dfs-item-name">{item.name}</div>
                                                                            {item.description && (
                                                                                <div className="dfs-item-desc">
                                                                                    {item.description}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="dfs-item-portion">
                                                                            {item.portion?.display || "-"}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="dfs-empty-note">
                                                                No detected food items.
                                                            </div>
                                                        )}
                                                    </div>

                                                    {metaChips.length > 0 && (
                                                        <div className="dfs-chip-row">
                                                            {metaChips.map((meta) => (
                                                                <span className="dfs-chip" key={`${foodLog.id}-${meta}`}>
                                                                    {meta}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {foodLog.ai_summary && (
                                                        <details className="dfs-details mt-2">
                                                            <summary>AI log summary</summary>
                                                            <div className="dfs-details-body">
                                                                {foodLog.ai_summary}
                                                            </div>
                                                        </details>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </>
    );
};
